// src/components/admin/PromoCodeAnalytics.tsx
import React, { useState, useEffect } from 'react';
import { 
  BarChart2, 
  RefreshCw, 
  Calendar, 
  TrendingUp, 
  Users, 
  AlertCircle,
  Download,
  Filter,
  PieChart,
  ChevronUp,
  ChevronDown,
  Clock
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import { PromoCodeAnalytics as PromoCodeAnalyticsType } from '../../types/admin';

const PromoCodeAnalytics: React.FC = () => {
  // Analytics data
  const [analytics, setAnalytics] = useState<PromoCodeAnalyticsType | null>(null);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Date filter state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Fetch analytics data
  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Create params object for date filters
      const params: Record<string, string> = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      
      const data = await adminApi.getPromoCodeAnalytics(params);
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch analytics data');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch data on mount and when filter changes
  useEffect(() => {
    fetchAnalytics();
  }, []); // Empty dependencies to only run on mount
  
  // Handle filter application
  const applyDateFilter = () => {
    fetchAnalytics();
  };
  
  // Clear date filters
  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    // Re-fetch with no date filters
    fetchAnalytics();
  };
  
  // Handle CSV export
  const handleExportCSV = () => {
    if (!analytics) return;
    
    // Create CSV for top performing codes
    let csv = 'Code,Redemptions,Conversion Rate\n';
    
    analytics.topPerformingCodes.forEach(code => {
      csv += `${code.code},${code.redemptions},${code.conversionRate}\n`;
    });
    
    // Create download link
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promo-code-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Calculate color based on conversion rate
  const getConversionRateColor = (rate: number) => {
    if (rate >= 0.5) return 'text-green-600';
    if (rate >= 0.25) return 'text-blue-600';
    return 'text-gray-600';
  };
  
  // Render loading state
  if (isLoading && !analytics) {
    return (
      <div className="p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-gray-600">Loading analytics data...</p>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-medium text-red-800 mb-1">Error Loading Analytics</h3>
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchAnalytics}
              className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded-md hover:bg-red-200 inline-flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Promo Code Analytics</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={fetchAnalytics}
            className="p-2 text-gray-600 hover:text-gray-800 border rounded-md hover:bg-gray-50"
            title="Refresh Data"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          {analytics && (
            <button
              onClick={handleExportCSV}
              className="px-3 py-2 text-gray-700 border rounded-md hover:bg-gray-50 flex items-center"
            >
              <Download className="h-4 w-4 mr-1.5" />
              Export
            </button>
          )}
        </div>
      </div>
      
      {/* Date filters */}
      <div className="bg-white p-4 rounded-lg border shadow-sm mb-6">
        <div className="flex items-center mb-4">
          <Filter className="h-5 w-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-medium">Date Range Filter</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <div className="flex items-center">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
              <Calendar className="h-5 w-5 text-gray-400 -ml-8" />
            </div>
          </div>
          
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <div className="flex items-center">
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
              <Calendar className="h-5 w-5 text-gray-400 -ml-8" />
            </div>
          </div>
          
          <div className="col-span-2 flex items-end space-x-2">
            <button
              onClick={applyDateFilter}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={isLoading}
            >
              Apply Filter
            </button>
            
            <button
              onClick={clearDateFilter}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={isLoading || (!startDate && !endDate)}
            >
              Clear Filter
            </button>
          </div>
        </div>
      </div>
      
      {analytics && (
        <>
          {/* Key metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <div className="flex items-center mb-2">
                <BarChart2 className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium">Total Codes</h3>
              </div>
              <div className="text-3xl font-bold">{analytics.totalCodes}</div>
              <div className="text-sm text-gray-500 mt-1">
                {analytics.activeCodes} active
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <div className="flex items-center mb-2">
                <Users className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium">Total Redemptions</h3>
              </div>
              <div className="text-3xl font-bold">{analytics.totalRedemptions}</div>
              <div className="text-sm text-gray-500 mt-1">
                Across all promo codes
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <div className="flex items-center mb-2">
                <TrendingUp className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium">Conversion Rate</h3>
              </div>
              <div className={`text-3xl font-bold ${getConversionRateColor(analytics.conversionRate)}`}>
                {(analytics.conversionRate * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-500 mt-1">
                From promo to paid
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <div className="flex items-center mb-2">
                <PieChart className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium">Plan Distribution</h3>
              </div>
              <div className="flex items-center space-x-2">
                <div className="text-lg">
                  <span className="font-medium text-blue-600">Medium:</span> {analytics.redemptionsByPlanType.medium || 0}
                </div>
                <div className="text-gray-300">|</div>
                <div className="text-lg">
                  <span className="font-medium text-purple-600">Premium:</span> {analytics.redemptionsByPlanType.premium || 0}
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full mt-2 overflow-hidden">
                <div 
                  className="h-full bg-blue-500"
                  style={{ 
                    width: `${analytics.redemptionsByPlanType.medium / 
                      (analytics.redemptionsByPlanType.medium + analytics.redemptionsByPlanType.premium) * 100}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
          
          {/* Top performing codes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="md:col-span-2 bg-white p-6 rounded-lg border shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Top Performing Codes</h3>
                <div className="text-sm text-gray-500">
                  By redemption count
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Redemptions
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Conversion Rate
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {analytics.topPerformingCodes.map((code, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {code.code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {code.redemptions}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={getConversionRateColor(code.conversionRate)}>
                            {(code.conversionRate * 100).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                    {analytics.topPerformingCodes.length === 0 && (
                      <tr>
                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                          No data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <div className="flex items-center mb-4">
                <Clock className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-medium">Recent Activity</h3>
              </div>
              
              <div className="space-y-4">
                {analytics.recentRedemptions.map((redemption, index) => (
                  <div key={index} className="border-b pb-3 last:border-b-0 last:pb-0">
                    <div className="text-sm font-medium">
                      Code: <span className="font-mono">{redemption.code}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Redeemed by {redemption.userName} ({new Date(redemption.redeemedAt).toLocaleDateString()})
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Plan: <span className="capitalize">{redemption.planType}</span>
                    </div>
                  </div>
                ))}
                {analytics.recentRedemptions.length === 0 && (
                  <div className="text-center text-gray-500 py-4">
                    No recent redemptions
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Time-based analysis */}
          <div className="bg-white p-6 rounded-lg border shadow-sm mb-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium">Redemption Patterns</h3>
              <div className="text-sm text-gray-500">
                Based on recent data
              </div>
            </div>
            
            {/* This would be a chart in a real application */}
            {/* For now, let's simulate it with a simple representation */}
            <div className="grid grid-cols-7 gap-1 mb-6">
              <div className="text-center text-xs text-gray-500 mb-1">Sun</div>
              <div className="text-center text-xs text-gray-500 mb-1">Mon</div>
              <div className="text-center text-xs text-gray-500 mb-1">Tue</div>
              <div className="text-center text-xs text-gray-500 mb-1">Wed</div>
              <div className="text-center text-xs text-gray-500 mb-1">Thu</div>
              <div className="text-center text-xs text-gray-500 mb-1">Fri</div>
              <div className="text-center text-xs text-gray-500 mb-1">Sat</div>
              
              {[0, 1, 2, 3, 4, 5, 6].map(day => {
                // Generate random intensity for demonstration
                const intensity = Math.floor(Math.random() * 5);
                let bgColor = 'bg-gray-100';
                if (intensity === 1) bgColor = 'bg-blue-100';
                if (intensity === 2) bgColor = 'bg-blue-200';
                if (intensity === 3) bgColor = 'bg-blue-300';
                if (intensity === 4) bgColor = 'bg-blue-400';
                
                return (
                  <div key={day} className={`h-8 ${bgColor} rounded-md`}></div>
                );
              })}
            </div>
            
            <div className="grid grid-cols-12 gap-1">
              <div className="text-center text-xs text-gray-500 mb-1">12am</div>
              <div className="text-center text-xs text-gray-500 mb-1">4am</div>
              <div className="text-center text-xs text-gray-500 mb-1">8am</div>
              <div className="text-center text-xs text-gray-500 mb-1">12pm</div>
              <div className="text-center text-xs text-gray-500 mb-1">4pm</div>
              <div className="text-center text-xs text-gray-500 mb-1">8pm</div>
              <div className="text-center text-xs text-gray-500 mb-1 col-span-6"></div>
              
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(hour => {
                // Generate random intensity for demonstration
                const intensity = Math.floor(Math.random() * 5);
                let bgColor = 'bg-gray-100';
                if (intensity === 1) bgColor = 'bg-green-100';
                if (intensity === 2) bgColor = 'bg-green-200';
                if (intensity === 3) bgColor = 'bg-green-300';
                if (intensity === 4) bgColor = 'bg-green-400';
                
                return (
                  <div key={hour} className={`h-8 ${bgColor} rounded-md`}></div>
                );
              })}
            </div>
            
            <div className="text-sm text-gray-500 text-center mt-4">
              Note: In a real implementation, this would be an interactive chart showing actual redemption patterns by day and hour.
            </div>
          </div>
          
          {/* Insights */}
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <h3 className="text-lg font-medium mb-4 text-blue-800">Key Insights</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="h-5 w-5 rounded-full bg-blue-100 border-2 border-blue-500 mt-0.5 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 className="font-medium text-blue-800">Conversion Performance</h4>
                  <p className="text-blue-700 text-sm mt-1">
                    {analytics.conversionRate >= 0.3 
                      ? "Your promo codes are performing well with a strong conversion rate." 
                      : "Consider optimizing your promo codes to improve conversion rates."}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="h-5 w-5 rounded-full bg-blue-100 border-2 border-blue-500 mt-0.5 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 className="font-medium text-blue-800">Plan Distribution</h4>
                  <p className="text-blue-700 text-sm mt-1">
                    {analytics.redemptionsByPlanType.premium > analytics.redemptionsByPlanType.medium 
                      ? "Premium plan promotions are performing well." 
                      : "Medium plan is more popular with users."}
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="h-5 w-5 rounded-full bg-blue-100 border-2 border-blue-500 mt-0.5 mr-3 flex-shrink-0"></div>
                <div>
                  <h4 className="font-medium text-blue-800">Recommendations</h4>
                  <p className="text-blue-700 text-sm mt-1">
                    Based on performance, consider creating more targeted promo codes with specific expiration dates to create urgency.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PromoCodeAnalytics;